import React, { useState, useRef, useEffect } from "react";

interface IOption {
    value: string;
    label: string;
}

interface ICustomSelectProps {
    prefix?: string;
    options: IOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
}

export const CustomSelect: React.FC<ICustomSelectProps> = ({
    prefix = "",
    options,
    value,
    onChange,
    placeholder,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const currentLabel = options.find((opt) => opt.value === value)?.label;
    const toggleOpen = () => setIsOpen(!isOpen);
    const handleOptionClick = (optionValue: string) => {
        onChange(optionValue);
        setIsOpen(false);
    };

    return (
        <div className="CustomSelect" ref={selectRef}>
            <button
                type="button"
                className="CustomSelect__button"
                onClick={toggleOpen}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                {prefix} <strong>{currentLabel || placeholder || ""}</strong>
                <span className="CustomSelect__arrow" />
            </button>
            {isOpen && (
                <ul className="CustomSelect__menu" role="listbox">
                    {options.map((opt) => {
                        const isSelected = opt.value === value;
                        return (
                            <li
                                key={opt.value}
                                role="option"
                                aria-selected={isSelected}
                                className={`CustomSelect__option ${isSelected ? "CustomSelect__option--selected" : ""}`}
                                onClick={() => handleOptionClick(opt.value)}
                            >
                                {opt.label}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};
